import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { Button } from "./button";
import { Badge } from "./badge";
import { Avatar, AvatarImage, AvatarFallback } from "./avatar";
import { Icon } from "@iconify/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { ChevronDown } from "lucide-react";

interface Transaction {
  id: string;
  purchaseId: string;
  buyer: {
    name: string;
    avatar?: string;
    avatarFallback: string;
  };
  invoice: string;
  purchaseDate: string;
  amount: number;
  paymentMethod: string;
  status: "Completed" | "Pending" | "Failed";
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

interface TransactionTableProps {
  title: string;
  transactions: Transaction[];
  onFilterChange?: (period: string) => void;
  onSelectAll?: (selected: boolean) => void;
  onSelectTransaction?: (id: string, selected: boolean) => void;
}

const TransactionTable: React.FC<TransactionTableProps> = ({
  title,
  transactions,
  onFilterChange,
  onSelectAll,
  onSelectTransaction,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState("This Month");

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    onFilterChange?.(period);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Completed":
        return "bg-green-500/10 text-green-400 text-xs rounded-[50px]";
      case "Pending":
        return "bg-yellow-500/10 text-yellow-400 text-xs rounded-[50px]";
      case "Failed":
        return "bg-red-500/10 text-red-400 text-xs rounded-[50px]";
      default:
        return "bg-gray-500/10 text-gray-400 text-xs rounded-[50px]";
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row justify-between items-center">
        <CardTitle>{title}</CardTitle>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-[5px] flex items-center gap-2"
            >
              {selectedPeriod}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handlePeriodChange("Today")}>
              Today
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePeriodChange("This Week")}>
              This Week
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePeriodChange("This Month")}>
              This Month
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePeriodChange("This Year")}>
              This Year
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background border border-surface-2">
              <tr>
                <th className="w-5 p-4">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                  />
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Purchase ID
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Buyer Name
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Invoice
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Purchase Date
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Total Amount
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Payment Method
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Payment Status
                </th>
                <th className="p-4 text-left text-sm font-medium text-surface-2 dark:text-surface-2">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-2">
              {transactions.map((transaction) => (
                <tr
                  key={transaction.id}
                  className="hover:bg-secondary transition-colors duration-200"
                >
                  <td className="p-4">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary dark:text-primary focus:ring-blue-500"
                      onChange={(e) =>
                        onSelectTransaction?.(transaction.id, e.target.checked)
                      }
                    />
                  </td>
                  <td className="p-4">
                    <a
                      href="#"
                      className="text-surface-2 dark:text-surface-2 hover:text-primary/80 font-medium"
                    >
                      #{transaction.purchaseId}
                    </a>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-8 h-8">
                        {transaction.buyer.avatar && (
                          <AvatarImage
                            src={transaction.buyer.avatar}
                            alt={transaction.buyer.name}
                          />
                        )}
                        <AvatarFallback>
                          {transaction.buyer.avatarFallback}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-surface-2 dark:text-surface-2">
                        {transaction.buyer.name}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-surface-2 dark:text-surface-2">
                    {transaction.invoice}
                  </td>
                  <td className="p-4 text-surface-2 dark:text-surface-2">
                    {transaction.purchaseDate}
                  </td>
                  <td className="p-4 text-surface-2 dark:text-surface-2">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="p-4 text-surface-2 dark:text-surface-2">
                    {transaction.paymentMethod}
                  </td>
                  <td className="p-4">
                    <Badge className={getStatusBadgeClass(transaction.status)}>
                      {transaction.status}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={transaction.onView}
                        className="text-surface-2 dark:text-surface-2 hover:text-primary/80 bg-background border border-surface-2 hover:bg-secondary"
                      >
                        <Icon icon="solar:eye-broken" className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransactionTable;
